import React, { useContext, useCallback, AriaAttributes } from 'react';
import { DropdownStateContext } from '../DropdownStateManager';
import BiDiv from '@wix/da-bi/pkg/Components/BiDiv';
import type { BiEvent } from '@wix/da-bi/pkg/events';

export interface Props {
  tabIndex?: number;
  className?: string;
  biData?: BiEvent;
  onOpenBiData?: BiEvent;
  onCloseBiData?: BiEvent;
  innerRef?: (node: HTMLElement | null) => void;
  onClick?: (e: React.MouseEvent) => void;
  'aria-label'?: AriaAttributes['aria-label'];
  'aria-owns'?: AriaAttributes['aria-owns'];
}

// A very simple toggle button implementation - this should cover 99% of use cases
const BasicDropdownToggle: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  tabIndex,
  className,
  onClick,
  biData,
  onOpenBiData = biData,
  onCloseBiData = biData,
  innerRef,
  'aria-label': ariaLabel,
  'aria-owns': ariaOwns,
}) => {
  const { isOpen, setToggleRef } = useContext(DropdownStateContext);
  const setRefs = useCallback(
    node => {
      setToggleRef(node);
      innerRef && innerRef(node);
    },
    [innerRef, setToggleRef]
  );

  return (
    <BiDiv
      tabIndex={tabIndex ?? 0}
      className={className}
      onClick={onClick}
      biData={isOpen ? onCloseBiData : onOpenBiData}
      innerRef={setRefs}
      role="button"
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-label={ariaLabel}
      aria-owns={ariaOwns}
    >
      {children}
    </BiDiv>
  );
};
BasicDropdownToggle.displayName = 'BasicDropdownToggle';

export default BasicDropdownToggle;
